import { useState } from "react";
import { mutate } from "swr";
import { adminUsersService } from "../../api_client";
import type { AdminUpdateUserDto } from "../../../api";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";

function extractError(err: unknown): string {
  const typed = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const raw = typed?.response?.data?.message ?? typed?.message;
  if (Array.isArray(raw)) return raw[0] ?? "Something went wrong";
  return raw ?? "Something went wrong";
}

async function invalidateAdminUserCaches() {
  await mutate(
    (key) => typeof key === "string" && key.startsWith("admin-user"),
  );
}

export function useUpdateAdminUser(id: string) {
  const [isLoading, setIsLoading] = useState(false);

  async function submit(payload: AdminUpdateUserDto): Promise<boolean> {
    setIsLoading(true);
    try {
      await adminUsersService.adminUpdateUser(id, payload);
      showSuccessToast("User updated successfully.");
      await invalidateAdminUserCaches();
      return true;
    } catch (err) {
      showErrorToast(extractError(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  }

  return { submit, isLoading };
}

export function useDeactivateAdminUser(id: string) {
  const [isLoading, setIsLoading] = useState(false);

  async function submit(): Promise<boolean> {
    setIsLoading(true);
    try {
      await adminUsersService.adminDeleteUser(id);
      showSuccessToast("User deactivated successfully.");
      await invalidateAdminUserCaches();
      return true;
    } catch (err) {
      showErrorToast(extractError(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  }

  return { submit, isLoading };
}
